import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSelect } from '@angular/material/select';
import moment from 'moment';
import { LimiteLegge } from '../../../../../models/controlli.model';
import {
  filtriDefault,
  IndiceRischio,
  isParametroClassificabile,
  nomeCategoriaTipoPunto,
  OpzioneComune,
  RischioAnaliticoFiltriValue,
  TipoPuntoCampionamentoLookup
} from '../../../../../models/rischio-analitico.model';
import { SplitViewService } from '@ebw-monorepo/utils';
import { RischioAnaliticoLegendaDialogComponent } from '../rischio-analitico-legenda-dialog/rischio-analitico-legenda-dialog.component';
import { RischioAnaliticoRiepilogoComponent } from '../rischio-analitico-riepilogo/rischio-analitico-riepilogo.component';
import { RischioAnaliticoStateService } from 'libs/ng-components/map/src/lib/services/rischio-analitico-state.service';

/**
 * Form filtri della sezione "Monitoraggio rischio analitico", condiviso tra i sotto-tab
 * Mappa e Riepilogo.
 */
@Component({
  selector: 'ebw-monorepo-rischio-analitico-filtri',
  templateUrl: './rischio-analitico-filtri.component.html',
  styleUrls: ['./rischio-analitico-filtri.component.scss']
})
export class RischioAnaliticoFiltriComponent {
  @Input() opzioniComune: OpzioneComune[] = [];
  @Input() opzioniTipologia: TipoPuntoCampionamentoLookup[] = [];
  @Input() opzioniAnaliti: LimiteLegge[] = [];
  @Input() mostraCriterio = true;
  @Input() poligonoAttivo = false;

  @Output() applica = new EventEmitter<RischioAnaliticoFiltriValue>();
  @Output() reset = new EventEmitter<void>();
  @Output() disegnaPoligono = new EventEmitter<void>();
  @Output() rimuoviPoligono = new EventEmitter<void>();

  isRiepilogoOpen: boolean = false;
  filtroAnaliti = '';
  filtroComuni = '';
  filtroTipologie = '';

  form: FormGroup;
  anniDisponibili: number[];

  constructor(
    private _splitViewService: SplitViewService,
    private _stato: RischioAnaliticoStateService,
    private _dialog: MatDialog
  ) {
    const valoriDefault = filtriDefault();
    this.anniDisponibili = this._generaAnniDisponibili(valoriDefault.annoRiferimento);
    // un FormControl per campo: FormBuilder.group leggerebbe gli array vuoti dei filtri multipli
    // come configurazione [valore, validatori] e li trasformerebbe in null
    this.form = new FormGroup(Object.fromEntries(Object.entries(valoriDefault).map(([campo, valore]) => [campo, new FormControl(valore)])));

    this._stato.closeRiepilogo$.subscribe(() => {
      this.isRiepilogoOpen = false;
      this.form.get('criterio').enable();
    });
  }

  get criterio(): IndiceRischio {
    return this.form.get('criterio').value;
  }

  get annoRiferimento(): number {
    return this.form.get('annoRiferimento').value;
  }

  // l'indice integrato usa sempre i 3 anni precedenti all'anno corrente (vedi resolveDateFilter nel backend)
  readonly annoCorrente = new Date().getFullYear();

  get opzioniAnalitiFiltrate(): LimiteLegge[] {
    const termine = this.filtroAnaliti.trim().toLowerCase();
    if (!termine) return this.opzioniAnaliti;
    return this.opzioniAnaliti.filter((parametro) => parametro.codice.toLowerCase().includes(termine));
  }

  parametroClassificabile(parametro: LimiteLegge): boolean {
    return isParametroClassificabile(parametro);
  }

  analitaCorrisponde(parametro: LimiteLegge): boolean {
    const termine = this.filtroAnaliti.trim().toLowerCase();
    return !termine || parametro.codice.toLowerCase().includes(termine);
  }

  /**
   * Svuota il campo di ricerca alla chiusura del pannello, cosi' non resta valorizzato alla riapertura.
   * All'apertura, riporta lo scroll del pannello in cima: mat-select di default scrolla fino alla prima
   * opzione selezionata, che con piu' analiti gia' scelti lascia la lista a meta' invece che dall'inizio.
   */
  onAnalitiPanelOpenedChange(aperto: boolean, select: MatSelect): void {
    if (!aperto) {
      this.filtroAnaliti = '';
      return;
    }
    this._scrollPannelloInCima(select);
  }

  get opzioniComuneFiltrate(): OpzioneComune[] {
    return this.opzioniComune.filter((comune) => this.comuneCorrisponde(comune));
  }

  // i comuni senza nome ("Comune non censito") si cercano per codice ISTAT
  comuneCorrisponde(comune: OpzioneComune): boolean {
    const termine = this.filtroComuni.trim().toLowerCase();
    if (!termine) return true;
    return (comune.nome ?? '').toLowerCase().includes(termine) || String(comune.codice).includes(termine);
  }

  onComuniPanelOpenedChange(aperto: boolean, select: MatSelect): void {
    if (!aperto) {
      this.filtroComuni = '';
      return;
    }
    this._scrollPannelloInCima(select);
  }

  get opzioniTipologiaFiltrate(): TipoPuntoCampionamentoLookup[] {
    return this.opzioniTipologia.filter((tipo) => this.tipologiaCorrisponde(tipo));
  }

  tipologiaCorrisponde(tipo: TipoPuntoCampionamentoLookup): boolean {
    const termine = this.filtroTipologie.trim().toLowerCase();
    if (!termine) return true;
    return [tipo.codice, tipo.descrizione, this.nomeCategoria(tipo.categoria)].some((testo) => (testo ?? '').toLowerCase().includes(termine));
  }

  onTipologiePanelOpenedChange(aperto: boolean, select: MatSelect): void {
    if (!aperto) {
      this.filtroTipologie = '';
      return;
    }
    this._scrollPannelloInCima(select);
  }

  private _scrollPannelloInCima(select: MatSelect): void {
    setTimeout(() => {
      if (select.panel?.nativeElement) {
        select.panel.nativeElement.scrollTop = 0;
      }
    });
  }

  nomeCategoria(categoria: string): string {
    return nomeCategoriaTipoPunto(categoria);
  }

  onApplica(): void {
    // getRawValue: con il Riepilogo aperto il criterio e' disabilitato (fisso sul 95° percentile)
    // e form.value lo escluderebbe, facendo partire la richiesta dei punti senza criterio
    const valore = this.form.getRawValue();
    this.applica.emit({
      ...valore,
      dataInizio: valore.dataInizio ? moment(valore.dataInizio).format('YYYY-MM-DD') : null,
      dataFine: valore.dataFine ? moment(valore.dataFine).format('YYYY-MM-DD') : null
    });
  }

  onReset(): void {
    if (this.isRiepilogoOpen) this.onToggleRiepilogo();
    this.form.reset(filtriDefault());
    this.reset.emit();
  }

  onToggleRiepilogo(): void {
    this.isRiepilogoOpen = !this.isRiepilogoOpen;

    if (this.isRiepilogoOpen) {
      this.form.get('criterio').disable();
      this._splitViewService.show(RischioAnaliticoRiepilogoComponent, 'riepilogo-rischio-analitico', null, {
        title: 'Riepilogo',
        icon: '',
        hideExpandActions: true
      });

      this._splitViewService.layoutConfChange({ isEnable: true, halfScreen: false, collapsed: false });
    } else {
      this.form.get('criterio').enable();
      this._splitViewService.destroy();
    }
  }

  onApriLegenda(): void {
    // heatmap/riepilogo sono sempre basati sul 95° percentile, indipendentemente dal criterio scelto per la mappa
    const criterio: IndiceRischio = this.isRiepilogoOpen ? 'q95' : this.criterio;
    this._dialog.open(RischioAnaliticoLegendaDialogComponent, { hasBackdrop: false, data: { criterio } });
  }

  private _generaAnniDisponibili(annoCorrente: number): number[] {
    const primoAnno = 2015;
    return Array.from({ length: annoCorrente - primoAnno + 1 }, (_, i) => annoCorrente - i);
  }
}
